import type { ProjectBrief, Task } from '@dyspatch/core';
import type { QueryClient } from '@tanstack/react-query';
import type { BriefDetail, ReviewResponse, TaskDetail } from '@/lib/api';
import { hubKeys } from '@/lib/query';

/**
 * What a gate verb does to the page before the hub has answered.
 *
 * Every write used to wait for the round trip and then invalidate the live set,
 * so the row you approved sat there looking un-approved for as long as the
 * refetch took, and on a phone that reads as a button that did nothing. The gate
 * is the product, and a gate you cannot tell you have cleared is a bad gate.
 *
 * So the verbs restate their row immediately. Three rules keep that honest:
 *
 * 1. **Restate, never invent.** An edit only moves a row to the state the verb
 *    is asking for. It never adds a row, never counts something the server has
 *    not been asked for, and never touches a cache the verb does not move.
 * 2. **Every edit hands back its undo.** The snapshot is taken before the write,
 *    and a refusal puts it back, so a 409 leaves the page exactly as it was
 *    rather than showing an approval that never happened.
 * 3. **The server still has the last word.** The invalidation runs on settle,
 *    success or failure, so the optimistic state is only ever the gap between
 *    the click and the truth.
 *
 * No verb here carries an "Undo" in its toast, and that is a fact about the hub
 * rather than an omission: `done` and `cancelled` are terminal in the task state
 * machine, a queued task cannot be put back into review, and there is no
 * un-approve. An undo button that files a second, different write is not an undo,
 * and offering one would promise the record can be edited when the whole point of
 * the record is that it cannot.
 */

/** Puts back what an edit changed. Called when the hub refuses the write. */
export type Rollback = () => void;

/** An edit to apply the moment the button is pressed. */
export type CacheEdit = (client: QueryClient) => Rollback;

/**
 * The caches a gate verb can move.
 *
 * Cancelled before an edit is applied: a refetch already in flight would land on
 * top of the optimistic state and put the row back, which looks exactly like the
 * write failing.
 *
 * The last two are prefixes covering every `['brief', id]` and `['task', id]`
 * entry, and they matter more than the lists do. The edits restate the detail
 * caches as well, and the detail pane is usually the thing being looked at when
 * the button is pressed, so an uncancelled refetch of it would undo the edit in
 * precisely the place the person is watching.
 */
export const OPTIMISTIC_KEYS = [
  hubKeys.briefs,
  hubKeys.tasks,
  hubKeys.review,
  ['brief'],
  ['task'],
] as const;

/**
 * Writes `next` into one cache entry and hands back the restore. A key nothing
 * has read yet holds nothing to restate, and editing it would seed the cache with
 * a row react-query never fetched.
 */
function patch<T>(client: QueryClient, key: readonly unknown[], next: (current: T) => T): Rollback {
  const current = client.getQueryData<T>(key);
  if (current === undefined) return () => {};
  client.setQueryData<T>(key, next(current));
  return () => {
    client.setQueryData<T>(key, current);
  };
}

/**
 * Restates one task in every `['brief', id]` entry that holds it.
 *
 * `taskMovesTo` is not told which job the task belongs to, so it has to look,
 * and until the job got a page (docs/JOB-LIFECYCLE.md 2.8) nothing noticed: the
 * plan and the step's own detail were two different routes. The job page draws
 * the plan list, the graph and the open step's detail at once - the first two
 * come from the brief detail, the third from the task detail - so a verb pressed
 * on the step moved the step and left its row and its node on the old status.
 * `OPTIMISTIC_KEYS` cancels this prefix, so nothing was going to put it right
 * until the settle refetch landed either.
 *
 * A key nothing has read yet is skipped, the same rule `patch` keeps.
 */
function patchBriefDetails(
  client: QueryClient,
  taskId: string,
  restate: (task: Task) => Task,
): Rollback {
  const rollbacks: Rollback[] = [];
  for (const [key, detail] of client.getQueriesData<BriefDetail>({ queryKey: ['brief'] })) {
    if (!detail?.tasks.some((task) => task.id === taskId)) continue;
    rollbacks.push(
      patch<BriefDetail>(client, key, (current) => ({
        ...current,
        tasks: current.tasks.map(restate),
      })),
    );
  }
  return all(rollbacks);
}

function all(rollbacks: Rollback[]): Rollback {
  return () => {
    for (const rollback of rollbacks) rollback();
  };
}

/**
 * A brief moves state: the list row restates, and so does its own pane, which is
 * usually the thing the person is looking at when they press the button.
 */
export function briefMovesTo(briefId: string, status: ProjectBrief['status']): CacheEdit {
  const restate = (brief: ProjectBrief) => (brief.id === briefId ? { ...brief, status } : brief);
  return (client) =>
    all([
      patch<ProjectBrief[]>(client, hubKeys.briefs, (briefs) => briefs.map(restate)),
      patch<BriefDetail>(client, hubKeys.brief(briefId), (detail) => ({
        ...detail,
        brief: restate(detail.brief),
      })),
    ]);
}

/**
 * A task moves state.
 *
 * The review queue is the one that has to be edited rather than restated: a task
 * that is no longer in `review` is no longer a gate, and leaving the row in the
 * queue would keep it on the Inbox and in both counts. `inReview` moves with it
 * so the strip and the sidebar do not disagree with the list they sit above.
 */
export function taskMovesTo(taskId: string, status: Task['status']): CacheEdit {
  const restate = (task: Task) => (task.id === taskId ? { ...task, status } : task);
  return (client) =>
    all([
      patch<Task[]>(client, hubKeys.tasks, (tasks) => tasks.map(restate)),
      patch<TaskDetail>(client, hubKeys.task(taskId), (detail) => ({
        ...detail,
        task: restate(detail.task),
      })),
      patch<ReviewResponse>(client, hubKeys.review, (review) => {
        if (status === 'review' || !review.queue.some((entry) => entry.id === taskId))
          return review;
        return {
          queue: review.queue.filter((entry) => entry.id !== taskId),
          metrics: { ...review.metrics, inReview: Math.max(0, review.metrics.inReview - 1) },
        };
      }),
      patchBriefDetails(client, taskId, restate),
    ]);
}
